import { useMemo, useCallback } from "react";
import { assertUnreachable } from "@aidkitorg/types/lib/typechecks";
import { Button } from "@aidkitorg/component-library";
import { useLocalizedStrings } from "../../../Localization";
import { useConnectionModal } from "./ConnectionModalContext";
import { SourceHeader } from "./SourceHeader";
import { type ConnectionModalState } from "./types";

interface ConnectionModalErrorPageProps {
  state: ConnectionModalState & { stage: "error" };
}

export function ConnectionModalErrorPage({
  state,
}: ConnectionModalErrorPageProps) {
  const L = useLocalizedStrings();
  const { getLocalizedString, onUpdateState, onExit } = useConnectionModal();

  const errorMessage = useMemo(() => {
    switch (state.errorStatus) {
      case "ExpiredToken":
        return getLocalizedString("expiredTokenErrorMessage", "expired_token_error_message");
      case "InvalidCredentials":
        return getLocalizedString("invalidCredentialsErrorMessage", "invalid_credentials_error_message");
      case "MfaQuestionInvalidAnswer":
      case "MfaQuestionNoAnswer":
        return getLocalizedString("incorrectAnswerErrorMessage", "incorrect_answer_error_message");
      case "Error":
        return getLocalizedString("genericErrorMessage", "generic_error_message");
      default:
        assertUnreachable(state.errorStatus);
    }
  }, [getLocalizedString]);

  // For InvalidCredentials we can safely return the user to the source page.
  // For other errors, we need to refresh the link token to proceed by closing the modal altogether.
  const isRecoverableError = state.errorStatus === "InvalidCredentials";

  const additionalText = getLocalizedString("additionalErrorMessageText");

  const handleClickBackToSource = useCallback(() => {
    onUpdateState({
      stage: "source",
      selectedSource: state.selectedSource,
    });
  }, [onUpdateState, state]);

  return <div>
    <SourceHeader source={state.selectedSource} />
    <div className="mt-2">{errorMessage}</div>
    <p>{additionalText}</p>
    <div className="flex mt-2">
      {isRecoverableError &&
        <Button onClick={handleClickBackToSource} variant="primary">
          {getLocalizedString("returnToSourcePageText", "return_to_source_page")}
        </Button>
      }
      <Button className="ml-auto" onClick={onExit} variant="secondary">
        {L.component_library.modal.close}
      </Button>
    </div>
  </div>
}
